// MARK://dhk8/admin-shell/components/Toast
// Sistema de toasts: <ToastProvider> monta un portal (stack arriba-derecha) y
// expone useToast() → toast(message, tone?). Auto-dismiss configurable. Tonos
// success/danger/info/warn (fill tint + dot + color de texto, NADA de edge-strip).
// z-index alto vía --pos-z-toast (fallback 9999).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react'

export type ToastTone = 'success' | 'danger' | 'info' | 'warn'

export interface ToastOptions {
  /** Milisegundos antes de auto-cerrar. 0 = no auto-cerrar. Default 4000. */
  duration?: number
}

interface ToastItem {
  id: number
  message: string
  tone: ToastTone
}

interface ToastContextValue {
  toast: (message: string, tone?: ToastTone, opts?: ToastOptions) => number
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TONE_VARS: Record<ToastTone, { fg: string; bg: string }> = {
  success: { fg: 'var(--pos-success)', bg: 'var(--pos-success-bg)' },
  danger: { fg: 'var(--pos-danger)', bg: 'var(--pos-danger-bg)' },
  info: { fg: 'var(--pos-info)', bg: 'var(--pos-info-bg)' },
  warn: { fg: 'var(--pos-warning)', bg: 'var(--pos-warning-bg)' },
}

const TONE_ICON: Record<ToastTone, typeof Info> = {
  success: CheckCircle2,
  danger: XCircle,
  info: Info,
  warn: AlertTriangle,
}

const DEFAULT_DURATION = 4000

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const seq = useRef(0)
  // Guardamos los timers para limpiarlos en unmount o en dismiss manual.
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id))
    const t = timers.current.get(id)
    if (t) { clearTimeout(t); timers.current.delete(id) }
  }, [])

  const toast = useCallback(
    (message: string, tone: ToastTone = 'info', opts?: ToastOptions) => {
      const id = ++seq.current
      setItems((prev) => [...prev, { id, message, tone }])
      const duration = opts?.duration ?? DEFAULT_DURATION
      if (duration > 0) {
        const handle = setTimeout(() => dismiss(id), duration)
        timers.current.set(id, handle)
      }
      return id
    },
    [dismiss],
  )

  useEffect(() => {
    const map = timers.current
    return () => { map.forEach((t) => clearTimeout(t)); map.clear() }
  }, [])

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div className="nui-toast-stack" role="region" aria-label="Notificaciones">
            {items.map((t) => {
              const { fg, bg } = TONE_VARS[t.tone]
              const Icon = TONE_ICON[t.tone]
              return (
                <div
                  key={t.id}
                  className="nui-toast"
                  role="status"
                  aria-live="polite"
                  style={{ background: bg, color: fg }}
                >
                  <Icon size={18} aria-hidden className="nui-toast__icon" />
                  <span className="nui-toast__msg">{t.message}</span>
                  <button
                    type="button"
                    className="nui-toast__close"
                    aria-label="Cerrar notificación"
                    onClick={() => dismiss(t.id)}
                  >
                    <X size={15} aria-hidden />
                  </button>
                </div>
              )
            })}
            <style>{`
              .nui-toast-stack {
                position: fixed;
                top: var(--pos-space-4, 16px);
                right: var(--pos-space-4, 16px);
                z-index: var(--pos-z-toast, 9999);
                display: flex;
                flex-direction: column;
                gap: var(--pos-space-2, 8px);
                max-width: min(380px, calc(100vw - 2 * var(--pos-space-4, 16px)));
                pointer-events: none;
              }
              .nui-toast {
                display: flex;
                align-items: flex-start;
                gap: var(--pos-space-2, 8px);
                padding: var(--pos-space-3, 12px) var(--pos-space-3, 12px);
                border-radius: var(--pos-radius-lg, 12px);
                box-shadow: var(--pos-shadow-3);
                border: 1px solid color-mix(in oklch, currentColor 22%, transparent);
                font-size: var(--pos-text-sm, 13px);
                font-weight: var(--pos-weight-medium, 500);
                pointer-events: auto;
                animation: nui-toast-in var(--pos-duration-base, 200ms) var(--pos-ease-out, ease) both;
              }
              .nui-toast__icon { flex-shrink: 0; margin-top: 1px; }
              .nui-toast__msg {
                flex: 1;
                min-width: 0;
                color: var(--pos-fg);
                line-height: var(--pos-leading-snug, 1.3);
                word-break: break-word;
              }
              .nui-toast__close {
                flex-shrink: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 22px;
                height: 22px;
                margin: -2px -2px 0 0;
                border: none;
                border-radius: var(--pos-radius-sm, 6px);
                background: transparent;
                color: var(--pos-fg-tertiary);
                cursor: pointer;
                -webkit-tap-highlight-color: transparent;
              }
              .nui-toast__close:hover {
                background: color-mix(in oklch, var(--pos-fg) 8%, transparent);
                color: var(--pos-fg);
              }
              @keyframes nui-toast-in {
                from { opacity: 0; transform: translateX(12px); }
                to { opacity: 1; transform: translateX(0); }
              }
              @media (prefers-reduced-motion: reduce) {
                .nui-toast { animation: none; }
              }
            `}</style>
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  )
}

/** Hook de toasts. Debe usarse dentro de <ToastProvider>. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast debe usarse dentro de <ToastProvider>')
  return ctx
}
